import type { IQueryPayload } from '@delasport/rule-engine-ui';
import type { IOption } from '@shared/form/types';

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  FIELD_FILTERS_ALLOWED_MODULE,
  type IInitialFilter,
  type ISearchParameters,
  OPERATOR_SORT,
  OPERATOR_TYPE,
} from '@features/filters/types';
import { getFieldBasedPreviousFiltersInitialValues } from '@features/filters/utils/getFieldBasedFiltersInitialValues';
import { hasPartialUpdates } from '@utils/hasPartialUpdates';

interface IInitialState {
  searchParameters: ISearchParameters;
  prevFilters: Record<string, IInitialFilter>;
  dialogState: {
    isConfirmBrandsDialogOpen: boolean;
  };
  activeCodeCampaign: {
    enabled: boolean;
    id: null | string;
    name: null | string;
  };
  codeCampaignConfig: {
    activeTab: number;
    queryError: boolean;
    isFormErrors: boolean;
    currentBrands: IOption[];
    activationAction: string;
    previousBrands: IOption[];
    brandChangeReason: string;
    hasBrandsWithoutAccess: boolean;
    manualCodeQuery?: IQueryPayload;
    depositCodeQuery?: IQueryPayload;
    isAppearanceAndContentErrors: boolean;
    currentManualCodeQuery?: IQueryPayload;
    currentDepositCodeQuery?: IQueryPayload;
  };
}

export const initialCodeCampaignSearchParameters = {
  page: 1,
  size: 10,
  filters: [{ value: true, field: 'enabled', type: OPERATOR_TYPE.EQUAL }],
  sorts: [
    {
      field: 'modified_at',
      order: OPERATOR_SORT.DESC,
    },
  ],
};

const initialState: IInitialState = {
  searchParameters: initialCodeCampaignSearchParameters,
  dialogState: {
    isConfirmBrandsDialogOpen: false,
  },
  activeCodeCampaign: {
    id: null,
    name: null,
    enabled: false,
  },
  prevFilters: getFieldBasedPreviousFiltersInitialValues(
    FIELD_FILTERS_ALLOWED_MODULE.CODE_CAMPAIGN
  ),
  codeCampaignConfig: {
    activeTab: 0,
    queryError: false,
    currentBrands: [],
    previousBrands: [],
    isFormErrors: false,
    activationAction: '',
    brandChangeReason: '',
    manualCodeQuery: undefined,
    depositCodeQuery: undefined,
    hasBrandsWithoutAccess: false,
    currentManualCodeQuery: undefined,
    currentDepositCodeQuery: undefined,
    isAppearanceAndContentErrors: false,
  },
};

const codeCampaignsSlice = createSlice({
  initialState,
  name: 'codeCampaignsSlice',
  reducers: {
    setPrevFilters(
      state: IInitialState,
      action: PayloadAction<Record<string, IInitialFilter>>
    ) {
      state.prevFilters = action.payload;
    },
    setActiveCodeCampaign(
      state: IInitialState,
      action: PayloadAction<IInitialState['activeCodeCampaign']>
    ) {
      state.activeCodeCampaign = action.payload;
    },
    setCampaignDialogState(
      state: IInitialState,
      action: PayloadAction<Partial<IInitialState['dialogState']>>
    ) {
      state.dialogState = { ...state.dialogState, ...action.payload };
    },
    setSearchParameters(
      state: IInitialState,
      action: PayloadAction<Partial<ISearchParameters>>
    ) {
      if (!hasPartialUpdates(state.searchParameters, action.payload)) {
        return;
      }

      state.searchParameters = { ...state.searchParameters, ...action.payload };
    },
    setCodeCampaignConfig(
      state: IInitialState,
      action: PayloadAction<Partial<IInitialState['codeCampaignConfig']>>
    ) {
      if (!hasPartialUpdates(state.codeCampaignConfig, action.payload)) {
        return;
      }

      Object.assign(state.codeCampaignConfig, action.payload);
    },
  },
});

export default codeCampaignsSlice.reducer;

export const {
  setPrevFilters,
  setSearchParameters,
  setCodeCampaignConfig,
  setActiveCodeCampaign,
  setCampaignDialogState,
} = codeCampaignsSlice.actions;
